(() => {
  'use strict';

  const onReady = (callback) => {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', callback, { once: true });
    else callback();
  };

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const searchFields = 'title,product_type,vendor,tag,variants.title,variants.sku';
  const normalise = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

  function enhanceSearch(container, fallbackLink, suffix) {
    if (!container || container.querySelector('[data-bw-global-search]')) return;

    const form = document.createElement('form');
    form.className = `bw-global-search bw-global-search--${suffix}`;
    form.method = 'get';
    form.action = `${root}search`;
    form.setAttribute('role', 'search');
    form.dataset.bwGlobalSearch = '';

    const inputId = `BathwiseGlobalSearch-${suffix}`;
    const resultsId = `BathwiseGlobalSearchResults-${suffix}`;
    form.innerHTML = `
      <label class="bw-visually-hidden" for="${inputId}">Search Bathwise products</label>
      <div class="bw-global-search__field">
        <input id="${inputId}" class="bw-global-search__input" type="search" role="combobox" aria-haspopup="listbox" name="q" autocomplete="off" autocapitalize="off" spellcheck="false" placeholder="Search products, ranges or SKU" aria-autocomplete="list" aria-controls="${resultsId}-listbox" aria-expanded="false">
        <button class="bw-global-search__submit" type="submit" aria-label="Search">Search</button>
      </div>
      <input type="hidden" name="type" value="product">
      <input type="hidden" name="options[prefix]" value="last">
      <input type="hidden" name="options[unavailable_products]" value="show">
      <div id="${resultsId}" class="bw-global-search__results" data-bw-global-search-results hidden></div>\n      <span class="bw-visually-hidden" data-bw-search-status role="status" aria-live="polite" aria-atomic="true"></span>
    `;

    if (fallbackLink) {
      fallbackLink.classList.add('bw-global-search__fallback');
      container.insertBefore(form, fallbackLink);
    } else {
      container.prepend(form);
    }
    container.classList.add('has-bw-global-search');

    const input = form.querySelector('.bw-global-search__input');
    const results = form.querySelector('[data-bw-global-search-results]');
    let timer = 0;
    let controller = null;
    let activeIndex = -1;

    const close = () => {
      window.clearTimeout(timer);
      if (controller) controller.abort();
      results.hidden = true;
      const status = form.querySelector('[data-bw-search-status]');
      if (status) status.textContent = '';
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      activeIndex = -1;
    };

    const options = () => Array.from(results.querySelectorAll('[data-bw-predictive-option]'));

    const setActive = (index) => {
      const items = options();
      if (!items.length) return;
      results.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      activeIndex = (index + items.length) % items.length;
      items.forEach((item, itemIndex) => {
        const active = itemIndex === activeIndex;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-selected', active ? 'true' : 'false');
        if (!item.id) item.id = `${resultsId}-option-${itemIndex}`;
      });
      input.setAttribute('aria-activedescendant', items[activeIndex].id);
      items[activeIndex].scrollIntoView({ block: 'nearest' });
    };

    const loadResults = async (term) => {
      if (term.trim().length < 2) {
        results.innerHTML = '';
        close();
        return;
      }

      if (controller) controller.abort();
      controller = new AbortController();
      const params = new URLSearchParams();
      params.set('q', term.trim());
      params.set('resources[type]', 'product');
      params.set('resources[limit]', '8');
      params.set('resources[options][prefix]', 'last');
      params.set('resources[options][unavailable_products]', 'show');
      params.set('resources[options][fields]', searchFields);
      params.set('section_id', 'bathwise-predictive-search');

      try {
        const response = await fetch(`${root}search/suggest?${params.toString()}`, {
          signal: controller.signal,
          headers: { Accept: 'text/html' }
        });
        if (!response.ok) throw new Error(`Predictive search returned ${response.status}`);
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const section = doc.querySelector('#shopify-section-bathwise-predictive-search');
        results.innerHTML = section ? section.innerHTML : html;
        activeIndex = -1;
        input.removeAttribute('aria-activedescendant');
        const listbox = results.querySelector('[role="listbox"]');
        if (listbox) {
          listbox.id = `${resultsId}-listbox`;
          input.setAttribute('aria-controls', listbox.id);
        } else {
          input.removeAttribute('aria-controls');
        }
        options().forEach((item, index) => {
          item.id = `${resultsId}-option-${index}`;
          item.setAttribute('aria-selected', 'false');
        });
        results.hidden = false;
        input.setAttribute('aria-expanded', listbox ? 'true' : 'false');
        const status = form.querySelector('[data-bw-search-status]');
        if (status) status.textContent = (results.querySelector('.bw-predictive__header, .bw-predictive__empty')?.textContent || '').trim().replace(/\s+/g, ' ');
      } catch (error) {
        if (error.name !== 'AbortError') close();
      }
    };

    input.addEventListener('input', () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => loadResults(input.value), 160);
    });

    input.addEventListener('keydown', (event) => {
      const items = options();
      if (event.key === 'ArrowDown' && items.length) {
        event.preventDefault();
        setActive(activeIndex + 1);
      } else if (event.key === 'ArrowUp' && items.length) {
        event.preventDefault();
        setActive(activeIndex - 1);
      } else if (event.key === 'Enter' && activeIndex >= 0 && items[activeIndex]) {
        event.preventDefault();
        items[activeIndex].click();
      } else if (event.key === 'Escape' && !results.hidden) {
        event.preventDefault();
        event.stopPropagation();
        close();
      }
    });

    form.addEventListener('focusout', (event) => {
      if (!form.contains(event.relatedTarget)) window.setTimeout(close, 80);
    });

    document.addEventListener('click', (event) => {
      if (!form.contains(event.target)) close();
    });
  }

  function initGlobalSearch() {
    const actions = document.querySelector('.bw-header__actions');
    if (actions) {
      const fallback = Array.from(actions.querySelectorAll('a')).find((link) => normalise(link.textContent) === 'search');
      enhanceSearch(actions, fallback, 'desktop');
    }

    const mobileNav = document.querySelector('[data-bw-nav]');
    if (mobileNav) {
      const fallback = Array.from(mobileNav.querySelectorAll('a')).find((link) => normalise(link.textContent) === 'search');
      enhanceSearch(mobileNav, fallback, 'mobile');
    }
  }

  function finishFromVariant(value) {
    const text = normalise(value);
    const finishes = [
      'brushed bronze', 'brushed brass', 'brushed nickel', 'polished nickel',
      'matt black', 'matte black', 'gunmetal grey', 'gunmetal', 'rose gold',
      'polished chrome', 'chrome', 'bronze', 'brass', 'nickel', 'black', 'white', 'gold'
    ];
    return finishes.find((finish) => text.includes(finish)) || '';
  }

  function reinforceVariantMedia(select) {
    const option = select.options[select.selectedIndex];
    if (!option) return;
    const gallery = document.querySelector('.bw-product-gallery, [data-bw-product-gallery]');
    if (!gallery) return;

    const clickableMedia = Array.from(gallery.querySelectorAll('button[data-media-id], a[data-media-id], [role="button"][data-media-id]'));
    const mediaId = option.dataset.mediaId || '';
    if (mediaId) {
      const direct = clickableMedia.find((item) => item.dataset.mediaId === mediaId);
      if (direct) direct.click();
      return;
    }

    const finish = finishFromVariant(option.dataset.title || option.textContent);
    if (!finish) return;
    const match = clickableMedia.find((item) => {
      const image = item.querySelector('img');
      const haystack = normalise(`${image?.alt || ''} ${image?.currentSrc || image?.src || ''} ${item.getAttribute('aria-label') || ''}`);
      return haystack.includes(finish);
    });
    if (match) match.click();
  }

  function initProductUx() {
    document.querySelectorAll('.bw-product-form').forEach((form) => {
      form.classList.add('bw-product-form--ux-tight');

      const calculator = form.querySelector('[data-bw-tile-calculator]');
      const submit = form.querySelector('button[type="submit"], input[type="submit"], [name="add"]');
      if (calculator && submit && submit.nextElementSibling !== calculator) {
        submit.insertAdjacentElement('afterend', calculator);
        calculator.classList.add('bw-tile-calculator--after-cta');
      }

      const variantSelect = form.querySelector('[data-bw-variant-select]');
      if (variantSelect) {
        const update = () => window.setTimeout(() => reinforceVariantMedia(variantSelect), 30);
        variantSelect.addEventListener('change', update);
        update();
      }
    });
  }

  onReady(() => {
    initGlobalSearch();
    initProductUx();
  });
})();
